import { useEffect, useId, useMemo, useState } from "react"

import { Plus, TableProperties } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { DashboardElement } from "../dashboard/catalog"

import { Button } from "@/components/ui/button"
import { DialogFooter } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

import { BindingsProvider } from "../dashboard/bindings-context"
import { DashboardElementRenderer } from "../dashboard/registry"
import { DashboardStateProvider } from "../state/DashboardStateProvider"
import {
  buildManualTile,
  MANUAL_TILE_TYPES,
  MANUAL_TILE_TYPE_I18N_KEYS,
  manualTileUsesTabularData,
  type ManualTileDraft,
  type ManualTileType,
  parseManualTileData,
  type TileWidth,
} from "../utils/manual-tile"

type ManualTileFormProps = {
  onCancel: () => void
  onCreate: (tile: DashboardElement) => void
}

const INITIAL_DRAFT: ManualTileDraft = {
  type: "BarChart",
  title: "",
  width: "full",
  rawData: "",
  categoryKey: "",
  valueKey: "",
  value: "",
  unit: "",
  content: "",
  progressMax: "100",
}

export function ManualTileForm({ onCancel, onCreate }: ManualTileFormProps) {
  const { t } = useTranslation()
  const fieldId = useId()
  const [draft, setDraft] = useState<ManualTileDraft>(INITIAL_DRAFT)
  const [submitted, setSubmitted] = useState(false)

  const parsedData = useMemo(
    () => (manualTileUsesTabularData(draft.type) ? parseManualTileData(draft.rawData) : null),
    [draft.rawData, draft.type]
  )
  const buildResult = useMemo(() => buildManualTile(draft), [draft])

  useEffect(() => {
    if (!parsedData?.success) return
    const [first, second] = parsedData.columns
    setDraft((current) => {
      const categoryKey = parsedData.columns.includes(current.categoryKey)
        ? current.categoryKey
        : first || ""
      const valueKey =
        parsedData.columns.includes(current.valueKey) && current.valueKey !== categoryKey
          ? current.valueKey
          : parsedData.columns.find((column) => column !== categoryKey) || second || ""
      if (categoryKey === current.categoryKey && valueKey === current.valueKey) return current
      return { ...current, categoryKey, valueKey }
    })
  }, [parsedData])

  function updateDraft<K extends keyof ManualTileDraft>(key: K, value: ManualTileDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  function handleCreate() {
    setSubmitted(true)
    if (!buildResult.success) return
    onCreate(buildResult.tile)
  }

  const showTitle = draft.type !== "Text"
  const showContent = draft.type === "Text" || draft.type === "Alert"
  const showValue = draft.type === "KPICard" || draft.type === "ProgressBar"
  const showPreview = buildResult.success
  const errorKey = !buildResult.success ? buildResult.error : null

  return (
    <div className="flex flex-col gap-4" data-testid="manual-tile-form">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-type`}>{t("dash.add_tile.manual.type")}</Label>
              <Select
                value={draft.type}
                onValueChange={(value) => updateDraft("type", value as ManualTileType)}
              >
                <SelectTrigger id={`${fieldId}-type`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_TILE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(MANUAL_TILE_TYPE_I18N_KEYS[type])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-width`}>{t("dash.add_tile.manual.width")}</Label>
              <Select
                value={draft.width}
                onValueChange={(value) => updateDraft("width", value as TileWidth)}
              >
                <SelectTrigger id={`${fieldId}-width`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">{t("dash.add_tile.manual.width_full")}</SelectItem>
                  <SelectItem value="half">{t("dash.add_tile.manual.width_half")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {showTitle && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-title`}>
                {t("dash.add_tile.manual.title")}
                {draft.type === "Alert" ? ` (${t("common.optional")})` : ""}
              </Label>
              <Input
                id={`${fieldId}-title`}
                value={draft.title}
                onChange={(event) => updateDraft("title", event.target.value)}
              />
            </div>
          )}

          {manualTileUsesTabularData(draft.type) && (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${fieldId}-data`}>{t("dash.add_tile.manual.data")}</Label>
                <Textarea
                  id={`${fieldId}-data`}
                  className="min-h-32 font-mono text-xs"
                  placeholder={t("dash.add_tile.manual.data_placeholder")}
                  value={draft.rawData}
                  onChange={(event) => updateDraft("rawData", event.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  {t("dash.add_tile.manual.data_hint")}
                </p>
              </div>

              {parsedData?.success && draft.type !== "Table" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`${fieldId}-category`}>
                      {t("dash.add_tile.manual.category")}
                    </Label>
                    <Select
                      value={draft.categoryKey}
                      onValueChange={(value) => updateDraft("categoryKey", value)}
                    >
                      <SelectTrigger id={`${fieldId}-category`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {parsedData.columns.map((column) => (
                          <SelectItem key={column} value={column}>
                            {column}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`${fieldId}-value-column`}>
                      {t("dash.add_tile.manual.value_column")}
                    </Label>
                    <Select
                      value={draft.valueKey}
                      onValueChange={(value) => updateDraft("valueKey", value)}
                    >
                      <SelectTrigger id={`${fieldId}-value-column`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {parsedData.columns
                          .filter((column) => column !== draft.categoryKey)
                          .map((column) => (
                            <SelectItem key={column} value={column}>
                              {column}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </>
          )}

          {showValue && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${fieldId}-value`}>{t("dash.add_tile.manual.value")}</Label>
                <Input
                  id={`${fieldId}-value`}
                  inputMode="decimal"
                  value={draft.value}
                  onChange={(event) => updateDraft("value", event.target.value)}
                />
              </div>
              {draft.type === "KPICard" ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`${fieldId}-unit`}>{t("dash.add_tile.manual.unit")}</Label>
                  <Input
                    id={`${fieldId}-unit`}
                    value={draft.unit}
                    onChange={(event) => updateDraft("unit", event.target.value)}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`${fieldId}-max`}>{t("dash.add_tile.manual.max")}</Label>
                  <Input
                    id={`${fieldId}-max`}
                    inputMode="decimal"
                    value={draft.progressMax}
                    onChange={(event) => updateDraft("progressMax", event.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {showContent && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-content`}>
                {draft.type === "Alert"
                  ? t("dash.add_tile.manual.message")
                  : t("dash.add_tile.manual.content")}
              </Label>
              <Textarea
                id={`${fieldId}-content`}
                className="min-h-28"
                value={draft.content}
                onChange={(event) => updateDraft("content", event.target.value)}
              />
            </div>
          )}

          {submitted && errorKey && (
            <p aria-live="polite" className="text-destructive text-sm" role="alert">
              {t(`dash.add_tile.manual.errors.${errorKey}`)}
            </p>
          )}
        </div>

        <div className="min-w-0">
          <Label>{t("dash.add_tile.manual.preview")}</Label>
          <div
            className="bg-muted/20 mt-1.5 min-h-44 rounded-lg border p-3"
            data-testid="manual-tile-preview"
          >
            {showPreview ? (
              <DashboardStateProvider initialState={{}}>
                <BindingsProvider bindings={undefined}>
                  <DashboardElementRenderer element={buildResult.tile} />
                </BindingsProvider>
              </DashboardStateProvider>
            ) : (
              <div className="text-muted-foreground flex min-h-36 flex-col items-center justify-center gap-2 text-center text-sm">
                <TableProperties className="size-8 opacity-60" />
                <p>{t("dash.add_tile.manual.preview_empty")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button data-testid="manual-tile-submit" onClick={handleCreate}>
          <Plus className="mr-1.5 size-4" />
          {t("dash.add_tile.manual.create")}
        </Button>
      </DialogFooter>
    </div>
  )
}
